import React, { useMemo, useRef } from 'react';
import ReactWebChat, { createDirectLine, createStyleSet, createStore } from 'botframework-webchat';
import { useWebChat } from '../../context';
import { useQuery } from 'react-query';
import { QueryClient, QueryClientProvider } from 'react-query';
import styled from '@emotion/styled';
import HeroCard from './HeroCard';

const WebChatStyled = styled.div`
  height: 100%;

  .webchat__send-box-text-box {
    padding: 0 !important;
  }


  .webchat__send-box-text-box__input {
    padding-left: 1.25em !important;
    margin-right: 0;
    background: var(--color-white) !important;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .webchat__send-box__button {
    background: var(--color-white) !important;
    margin: 0;
  }
  .webchat__basic-transcript__scrollable {
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
  }

  .webchat__bubble {
    min-width: 100%;

    p {
      font-weight: normal !important;

      font-size: 14px !important;

      @media screen and (min-width: 640px) {
        font-size: 16px !important;
      }

      line-height: 1.5em !important;
      white-space: normal !important;
      word-break: initial !important;
      word-wrap: initial !important;
    }
  }

  .webchat__bubble__content > .markdown,
  .webchat__bubble__content > .plain {
    padding: 1.063em 19px 1.063em 1.313em !important;
  }

  .webchat__bubble__content > .markdown a {
    font-size: 14px !important;

    @media screen and (min-width: 640px) {
      font-size: 16px !important;
    }
  }

  .ac-anchor {
    font-size: 14px !important;

    @media screen and (min-width: 640px) {
      font-size: 16px !important;
    }
  }

  .webchat__send-box__main {
    padding-top: 1.25em;
    padding-left: 1.5em;
    padding-right: 1.5em;
  }

  .webchat__basic-transcript__activity--from-bot .webchat__basic-transcript__activity-box {
    margin-right: 2.375em;
  }

  .webchat__bubble--from-user .webchat__bubble__content {
    margin-right: 1.25em !important;
    margin-left: 4.875em !important;
  }

  .webchat__stacked-layout--from-user {
    .webchat__stacked-layout__avatar-gutter {
      display: none !important;
    }
  }

  .webchat__suggested-action__button {
    padding: 8px 18px 8px  18px !important;
    transition: background-color 0.2s ease-in-out;

    @media screen and (min-width: 640px) {
      padding: 1.063em 1.313em !important;
    }

    &:hover {
      background-color: var(--color-primary-dark) !important;
    }
  }

  .webchat__stacked-layout__status,
  .webchat__stacked-layout__status span {
    font-size: 13px !important;
  }

  .webchat__suggested-action {
    margin: 0.5em 1.5em !important;
  }

  .webchat__stacked-layout__avatar-gutter {
    margin-left: 0.5em;
    margin-right: 9px;
  }

  .webchat__send-icon {
    display: none;
  }

  .webchat__send-box__button {
    cursor: pointer;

    &:hover {
      &:after {
        filter: brightness(0.75);
      }
    }

    &:after {
      font-size: 16px;
      transition: filter 0.2s ease-in-out;
      filter: brightness(1);
      content: '';
      display block;
      width: 1.625em;
      height: 1.375em;
      background-image: url("data:image/svg+xml,%3Csvg width='26' height='22' viewBox='0 0 26 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.13493 11.9431L10.8121 11L8.13493 10.0568L4.13875 8.64889L1.34112 1.37411L23.5546 11L1.34111 20.6258L4.13875 13.351L8.13493 11.9431ZM0.395809 21.0354C0.395885 21.0354 0.395961 21.0354 0.396036 21.0353L0.395809 21.0354Z' stroke='%230094D7' stroke-width='2'/%3E%3C/svg%3E%0A");
    }
  }

  span.webchat__suggested-action__text {
    font-style: normal !important;
    font-weight: bold !important;
    font-size: 14px !important;

    @media screen and (min-width: 640px) {
      font-size: 16px !important;
    }

    line-height: 1.5em !important;
  }

  .webchat__suggested-action__text.webchat__suggested-actions__button-text-stacked-text-wrap {
    display: flex;
    justify-content: center;
  }

  .webchat__initialsAvatar {
    display: none;
  }

  .webchat__imageAvatar {
    max-width: 2.25em;
    max-height: 2.25em;
  }

  .ac-textBlock,
  .ac-container,
  .ac-container p {
    font-size: 14px !important;

    @media screen and (min-width: 640px) {
      font-size: 16px !important;
    }
  }

  button.ac-pushButton {
    width: 14.438em !important;
    height: auto !important;
    margin: 0.938em 0px 0px 0px !important;
    padding: 0 !important;
    background: var(--color-white);
    border: 2px solid var(--color-primary) !important;
    box-sizing: border-box;
    border-radius: 4px !important;
    transition: background-color 0.2s ease-in-out;

    & > div {
      padding: 8px 18px 8px  18px !important;
      white-space: normal !important;
      color: var(--color-primary) !important;
      font-style: normal !important;
      font-weight: bold !important;
      font-size: 14px !important;

      @media screen and (min-width: 640px) {
        font-size: 16px !important;
        padding: 1.063em 1.313em !important;
      }

      line-height: 1.5em !important;
      letter-spacing: -0.3px !important;
      pointer-events: none;
    }

    &:hover {
      background-color: var(--color-primary) !important;
      & > div {
        color: var(--color-white) !important;
      }
    }
  }
`;

const Chat = () => {
  const { setNewMessage, token, setTab, lang, setSpinnerDisplayed, shadowRootId, reduxStore } = useWebChat();
  const isTelemetryInit = useRef(false);

  const directLine = useMemo(() => createDirectLine({
    webSocket: true,
    token,
    conversationStartProperties: { locale: lang }
  }), [token, lang]);

  const styleSet = useMemo(
    () =>
      createStyleSet({
        bubbleBackground: 'var(--color-primary-light)',
        bubbleFromUserBackground: 'var(--color-primary-dark)',
        bubbleBorderWidth: '0',
        bubbleBorderRadius: '4px',
        bubbleFromUserBorderRadius: '4px',
        bubbleFromUserBorderWidth: '0',
        bubbleFromUserTextColor: 'var(--color-white)',
        backgroundColor: 'var(--color-white)',
        sendBoxBackground: 'var(--color-primary-dark)',
        sendBoxButtonColorOnDisabled: '#828282',
        suggestedActionBorderRadius: '4px',
        suggestedActionTextColor: 'var(--color-white)',
        suggestedActionBackground: 'var(--color-primary)',
        suggestedActionBorderWidth: '0',
        sendBoxHeight: 56,
        sendBoxMaxHeight: 56,
        sendBoxTextColor: 'var(--color-black)',
        sendBoxBorderBottom: '3.375em',
        sendBoxBorderLeft: '1.5em',
        sendBoxBorderRight: '1.5em',
        sendBoxBorderTop: '1.25em',
        sendBoxPlaceholderColor: '#828282',
        paddingRegular: '0.25em',
        paddingWide: '0.5em',
        botAvatarInitials: '',
        cardPushButtonBackgroundColor: 'var(--color-white)',
        cardPushButtonTextColor: 'var(--color-primary)'
      }),
    []
  );

  const store = useMemo(
    () =>
      createStore({}, ({ dispatch }) => next => action => {
        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY' && action.payload.activity.type === 'event' && action.payload.activity.value === 'VIEW_FAQ') {
          setTab(false);
          setSpinnerDisplayed(true);
        }

        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY' && action.payload.activity.type === 'event' && action.payload.activity.value === 'REQUEST_USER_LOCATION') {
          dispatch({
            type: 'WEB_CHAT/SEND_EVENT',
            payload: {
              name: `userlocation`,
              value: {
                href: window.location.href,
                pathname: window.location.pathname,
                hostname: window.location.hostname,
                browserLanguage: window.navigator.language,
              },
            },
          });
        }

        if (action.type === 'WEB_CHAT/SET_SUGGESTED_ACTIONS' && action.payload.suggestedActions[0]?.type === 'imBack' ) {

          if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            const css = document.querySelectorAll('[data-emotion^="webchat--css"], [data-emotion^="react-scroll-to-bottom--css"]');

            if (css.length > 0) {
              window.requestAnimationFrame(() => {
                const shadow = document.querySelector(`#${shadowRootId.current.id}`).shadowRoot.querySelector('.shadow-style-container');
                css.forEach(x => {
                  shadow?.appendChild(x);
                });
              })
            }
          } else {

            const cssWebChat = [...document.querySelectorAll('[data-emotion^="webchat--css"]')].flatMap(({ sheet }) => [...sheet.cssRules].map(rules => rules.cssText)).join('\n');
            const cssSTB = [...document.querySelectorAll('[data-emotion^="react-scroll-to-bottom--css"]')].flatMap(({ sheet }) => [...sheet.cssRules].map(rules => rules.cssText)).join('\n');
            const css = `${cssWebChat} ${cssSTB}`;

            if (css) {
              window.requestAnimationFrame(() => {
                const currentStyle = document.querySelector(`#${shadowRootId.current.id}`).shadowRoot.querySelector('[data-css="webchat"]');
                const shadow = document.querySelector(`#${shadowRootId.current.id}`).shadowRoot.querySelector('.shadow-style-container');

                if (currentStyle == null) {
                  shadow?.insertAdjacentHTML("beforeend", `<style data-css="webchat">${cssWebChat} ${cssSTB}</style>`);
                } else if (css !== currentStyle.innerHTML) {
                  currentStyle.innerHTML = `${cssWebChat} ${cssSTB}`;
                }
              })
            }
          }

          setSpinnerDisplayed(false);
        }

        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
          if (action.payload.activity.from.role === 'bot') {
            setNewMessage(true);
          }
        }

        return next(action);
      }),
    [setNewMessage, setTab, setSpinnerDisplayed, shadowRootId]
  );

  reduxStore.current = store;

  const attachmentMiddleware = () => next => card => {

    switch (card.attachment.contentType) {
      case 'application/vnd.microsoft.card.hero':
        return <HeroCard next={next} card={card} />
      default:
        return next(card);
    }
  };

  const handleTelemetry = (e) => {
    const { name } = e;
    const { conversationId } = directLine;
    const { webchat_userid } = localStorage;
    if (name === 'init' && conversationId && webchat_userid && !isTelemetryInit.current) {
      isTelemetryInit.current = true;
      window.dataLayer?.push({
        'event': name,
        'eventName': 'chatbot_conversation_start',
        'eventType': 'chatbot',
        'conversationId': conversationId,
        'userId': webchat_userid
      })
    }
  }

  return (
    <WebChatStyled>
      <ReactWebChat
        className="web-chat"
        styleSet={styleSet}
        styleOptions={{
          suggestedActionLayout: 'stacked',
          suggestedActionsStackedLayoutButtonTextWrap: true,
          hideUploadButton: true,
          avatarSize: 36,
          cardPushButtonBackgroundColor: 'var(--color-primary-dark)',
          cardPushButtonTextColor: 'var(--color-white)',
          botAvatarInitials: 'Bot',
          userAvatarInitials: 'User',
          botAvatarImage: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAASMSURBVHgBxVhLbBtFGP5m/YqT+EFoKqqm2OYhEKhKLKXQA4XkABxJJHJEbSUuqKpaLhSBhOkFTqi06gVRRIETAqkpCFBVpCQIRAtUdUB9gFC9DklKwXWc2En9iHf4Z/2K7XV2ltbtJ6092vln9tv/Nf+/DBYRu8GDShEjjKGfA0Pg8IPRJcCRorFKcyrXMKUVMB66i6kWtqflskQyfJdNwU7OiYQ1RBnH4Xu72XEZYVNCsTQfUhR8SMMgbg4qETtoRqwlodgC99tciJBG9uMWgsz5bjGHg2TKlOE8jMkEFSdO0HAA7YGq5TFs5F+sBZkJ3LyJ/hcp1kDGT2TO3wYyFUTLpKrmU9bOCp9pRWY2lcW3fyRgBUvZVbM1A+VnVlHVkAhrhenRZLjxc8fOYW4xi707Atj7ZBAyGD56VmqNppGWPGxSjKsaIjKR9TZfyq3q/163HZZhklzKaaUmup52Kjj9ewIf/zSLT14oBd5SXsOpmTQuXc9hMV/U7z3S48L2TV14lP4FLl3L4NUvL+Pki4Mw5cyxW+Qo/XVJTfvMFvg6apo5Ek3i/QsJnZQRtt/TiXd2bIKX1nhdkhqlU4B+jysizElPUvlGEDjw3RKOTLcmI3Dm7xWMfHEVp+M5yEIcSfOcB+xwYER20a8FOwqJgqw43ruYQcjdIS2fW8aoQqn8KRnhmTyD5nLCKn7LKetqcy1sVEHYyZmCMmf+oWgtn4Q8Try01Qu/y2YoOzW3gq9iJU0KMh9cSOLl8AaYQZQzdiITNBP8kXxiNlN6wOYuG4493QePQ0GKoivocdTJRhM57HrYh6PTy3j7XCkBC5+ShF/kIb+Z1JmrtQ0f6rFhS7cdwydmEP40Vid38kpGvzd+JY3R+9219RYIWc5y85mSP0Qe2wA1na+b6+91YX9/DwZ6OzC/XKyb+4s0vKXbYbq/ICT0aqqlCi4vrOKtX5J4bbCnaU6Y79ATG8lsWeyZWqib8zltErsjZSdHSjETQo1v9ubPCbx+9p8m/6lATRfQ6+qEQymR6KP1XqcCCah2SkhTzMSxnwl0A98bP1gGInPLgJwhLkwWpWvneoJeUvfYAz589udi3f2hzZ2IbKuFcypXxOg3c03rZUJeB8ekAhvGZWTfeHyjrvq1EL4S9Dp0YuKavp4zJNMn4cw6iIueEmcyfIIz8/bmYjKLyA83cD75LwpaKdqEH43c59HJTc7Vwlv40NiDPhzY5oMkooEuFpYuPyq4tqJh7GsV8YaQb8Serb14ZfBuyKJSflQPjfgKj4HL19Kfkz+diqf1pCeOBxFFfd1OPEsB8Dz5m7SZdDZQA50sVBqWUW4IJ3AHUNGOGFeTg6hpyS0O4zZDa2izjdogoaV2NYj1IFNpboRDrEUbJPojTcGoEES7Icgw6jZYfUvdlM9DbqYLtpUUowZRkHE3t9KGB4xOKodwO3xK+AyZyZCMgPnnGJGjFOrZLKQEGD9J1YrYXWkIW4tJQhAj4X2MWXN4rp9P+OiWfbBqIlb+pMdFc0BaK1cKlfIlVS5nJmluWqOzqZVpWuE/iam9xBRuFOoAAAAASUVORK5CYII=",
        }}
        locale={lang}
        directLine={directLine}
        attachmentMiddleware={attachmentMiddleware}
        username='Web Chat User'
        store={store}
        onTelemetry={handleTelemetry}
      />
    </WebChatStyled>
  );
}

const WebChat = () => {
  const { fetchToken, reset } = useWebChat();

  const { isLoading, error, data } = useQuery('fetchToken', async () => {
    //if (storageToken) {
    //  setToken(storageToken)
    //  return storageToken;
    //}

    const token = await fetchToken();
    return token;
  });

  if (error) {
    return (
      <div>
        Something went wrong...
      </div>
    )
  }

  if (isLoading || data == null) {
    return <></>
  }

  return (
    <>
      {!reset && <Chat />}
    </>
  )
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: true,
      staleTime: Infinity,
    }
  }
})

const WebchatQueryClient = () => (
  <QueryClientProvider client={queryClient}>
    <WebChat />
  </QueryClientProvider>
)

export default WebchatQueryClient;
